import { useState, useEffect } from 'react'
import { ref, get, set, push, remove, update } from '../lib/firebase-database'
import { ref as storageRef, uploadBytes, getDownloadURL } from '../lib/firebase-storage'
import { db, storage } from '../firebase'
import { Layout } from './Layout'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Switch } from './ui/switch'
import { Check, Trash2, MoveUp, MoveDown, Plus, ImagePlus, Loader2 } from 'lucide-react'
import { useToast } from './ui/use-toast'
import { MaterialIcon } from "./MaterialIcon"

const facilityOptions = [
  { id: "wifi", label: "WiFi", icon: () => <MaterialIcon name="wifi" /> },
  { id: "tv", label: "TV", icon: () => <MaterialIcon name="tv" /> },
  { id: "ac", label: "AC", icon: () => <MaterialIcon name="ac_unit" /> },
  { id: "kitchen", label: "Kitchen", icon: () => <MaterialIcon name="kitchen" /> },
  { id: "parking", label: "Parking", icon: () => <MaterialIcon name="local_parking" /> },
  { id: "petFriendly", label: "Pet Friendly", icon: () => <MaterialIcon name="pets" /> },
  { id: "pool", label: "Swimming Pool", icon: () => <MaterialIcon name="pool" /> },
  { id: "gym", label: "Gym", icon: () => <MaterialIcon name="fitness_center" /> },
  { id: "gamesRoom", label: "Games Room", icon: () => <MaterialIcon name="sports_esports" /> },
  { id: "garden", label: "Garden Area", icon: () => <MaterialIcon name="yard" /> },
  { id: "mountainView", label: "Mountain View", icon: () => <MaterialIcon name="landscape" /> },
  { id: "beachView", label: "Beach View", icon: () => <MaterialIcon name="beach_access" /> },
  { id: "alcoholAllowed", label: "Alcohol Allowed", icon: () => <MaterialIcon name="local_bar" /> },
  { id: "noDrinking", label: "No Drinking", icon: () => <MaterialIcon name="no_drinks" /> },
  { id: "birthday", label: "Birthday Celebrations", icon: () => <MaterialIcon name="cake" /> },
  { id: "party", label: "Party Celebrations", icon: () => <MaterialIcon name="celebration" /> },
  { id: "indoorGames", label: "Indoor Games", icon: () => <MaterialIcon name="sports_kabaddi" /> },
  { id: "bbq", label: "BBQ", icon: () => <MaterialIcon name="outdoor_grill" /> },
  { id: "meditation", label: "Meditation Area", icon: () => <MaterialIcon name="self_improvement" /> },
  { id: "spa", label: "Spa", icon: () => <MaterialIcon name="spa" /> },
  { id: "cottages", label: "Cottages", icon: () => <MaterialIcon name="cottage" /> },
  { id: "bungalow", label: "Bungalow", icon: () => <MaterialIcon name="foundation" /> },
  { id: "kingBed", label: "King Size Bed", icon: () => <MaterialIcon name="king_bed" /> },
  { id: "golfCourse", label: "Golf Course", icon: () => <MaterialIcon name="golf_course" /> },
  { id: "hiking", label: "Hiking", icon: () => <MaterialIcon name="hiking" /> },
  { id: "turf", label: "Turf", icon: () => <MaterialIcon name="sports_cricket" /> },
  { id: "cctv", label: "CCTV", icon: () => <MaterialIcon name="videocam" /> },
  { id: "powerBackup", label: "24/7 Power Backup", icon: () => <MaterialIcon name="bolt" /> },
  { id: "preWedding", label: "Pre-Wedding Shoot", icon: () => <MaterialIcon name="video_camera_front" /> },
  { id: "wedding", label: "Wedding", icon: () => <MaterialIcon name="face_3" /> },
  { id: "bigFunction", label: "Big Function", icon: () => <MaterialIcon name="groups" /> },
  { id: "towels", label: "Towels", icon: () => <MaterialIcon name="dry_cleaning" /> },
  { id: "dinner", label: "Dinner", icon: () => <MaterialIcon name="restaurant" /> },
].map((facility) => ({
  ...facility,
  icon: () => <div className="text-gray-600">{facility.icon()}</div>,
}))

export default function SpecialOccasionsPage() {
  const [occasions, setOccasions] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const { toast } = useToast()
  
  const [newOccasion, setNewOccasion] = useState({
    title: '',
    image: null,
    imagePreview: '',
    isVisible: true,
    facilities: []
  })

  useEffect(() => {
    fetchOccasions()
  }, [])

  const fetchOccasions = async () => {
    try {
      const occasionsRef = ref(db, 'specialOccasions')
      const snapshot = await get(occasionsRef)
      if (snapshot.exists()) {
        const data = snapshot.val()
        const occasionsList = Object.entries(data).map(([id, value]) => ({
          id,
          ...value,
          isVisible: value.isVisible ?? true
        }))
        // Sort by order
        occasionsList.sort((a, b) => (a.order || 0) - (b.order || 0))
        setOccasions(occasionsList)
      }
      setLoading(false)
    } catch (error) {
      console.error('Error fetching occasions:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load special occasions"
      })
      setLoading(false)
    }
  }

  const handleImageChange = async (e, id = null) => {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Image size should be less than 5MB"
      })
      return
    }

    if (id) {
      // Edit existing occasion
      const updatedOccasions = occasions.map(occasion => {
        if (occasion.id === id) {
          return {
            ...occasion,
            imageFile: file,
            imagePreview: URL.createObjectURL(file)
          }
        }
        return occasion
      })
      setOccasions(updatedOccasions)
    } else {
      // New occasion
      setNewOccasion({
        ...newOccasion,
        image: file,
        imagePreview: URL.createObjectURL(file)
      })
    }
  }

  const handleToggleVisibility = async (id) => {
    try {
      const occasion = occasions.find(o => o.id === id)
      const updatedOccasions = occasions.map(o => 
        o.id === id ? { ...o, isVisible: !o.isVisible } : o
      )
      
      await update(ref(db, `specialOccasions/${id}`), {
        isVisible: !occasion.isVisible
      })
      
      setOccasions(updatedOccasions)
      toast({
        title: "Success",
        description: `Occasion ${!occasion.isVisible ? 'shown' : 'hidden'} successfully`
      })
    } catch (error) {
      console.error('Error toggling visibility:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update visibility"
      })
    }
  }

  const handleAddOccasion = async (e) => {
    e.preventDefault()
    if (!newOccasion.title || !newOccasion.image) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill all fields"
      })
      return
    }

    setUploading(true)
    try {
      const imageRef = storageRef(storage, `specialOccasions/${Date.now()}-${newOccasion.image.name}`)
      await uploadBytes(imageRef, newOccasion.image)
      const imageUrl = await getDownloadURL(imageRef)

      const occasionsRef = ref(db, 'specialOccasions')
      const newOccasionRef = push(occasionsRef)
      await set(newOccasionRef, {
        title: newOccasion.title,
        imageUrl,
        isVisible: true,
        order: occasions.length,
        facilities: newOccasion.facilities
      })

      setNewOccasion({
        title: '',
        image: null,
        imagePreview: '',
        isVisible: true,
        facilities: []
      })
      
      fetchOccasions()
      toast({
        title: "Success",
        description: "Special occasion added successfully"
      })
    } catch (error) {
      console.error('Error adding occasion:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add special occasion"
      })
    } finally {
      setUploading(false)
    }
  }

  const handleUpdateOrder = async (id, direction) => {
    const currentIndex = occasions.findIndex(o => o.id === id)
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1

    if (newIndex < 0 || newIndex >= occasions.length) return

    const reorderedOccasions = [...occasions]
    const [moved] = reorderedOccasions.splice(currentIndex, 1)
    reorderedOccasions.splice(newIndex, 0, moved)

    try {
      const updates = {}
      reorderedOccasions.forEach((occasion, index) => {
        updates[`specialOccasions/${occasion.id}/order`] = index
      })
      await set(ref(db), updates)
      setOccasions(reorderedOccasions)
    } catch (error) {
      console.error('Error updating order:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update order"
      })
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this occasion?')) return

    try {
      await remove(ref(db, `specialOccasions/${id}`))
      setOccasions(occasions.filter(o => o.id !== id))
      toast({
        title: "Success",
        description: "Special occasion deleted successfully"
      })
    } catch (error) {
      console.error('Error deleting occasion:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete special occasion"
      })
    }
  }

  const handleUpdateOccasion = async (id) => {
    const occasion = occasions.find(o => o.id === id)
    if (!occasion) return

    setUploading(true)
    try {
      let imageUrl = occasion.imageUrl

      // If there's a new image, upload it
      if (occasion.imageFile) {
        const imageRef = storageRef(storage, `specialOccasions/${Date.now()}-${occasion.imageFile.name}`)
        await uploadBytes(imageRef, occasion.imageFile)
        imageUrl = await getDownloadURL(imageRef)
      }

      // Update the occasion in Firebase
      await update(ref(db, `specialOccasions/${id}`), {
        title: occasion.title,
        imageUrl,
        isVisible: occasion.isVisible,
        order: occasion.order
      })

      toast({
        title: "Success",
        description: "Occasion updated successfully"
      })

      // Refresh occasions
      fetchOccasions()
    } catch (error) {
      console.error('Error updating occasion:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update occasion"
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-2xl font-bold mb-6">Special Occasions Management</h1>

        {/* Add New Occasion Form */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h2 className="text-xl font-semibold mb-4">Add New Occasion</h2>
          <form onSubmit={handleAddOccasion} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <Input
                type="text"
                value={newOccasion.title}
                onChange={(e) => setNewOccasion({ ...newOccasion, title: e.target.value })}
                placeholder="Enter occasion title"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Image</label>
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('newImage').click()}
                >
                  <ImagePlus className="h-4 w-4 mr-2" />
                  Select Image
                </Button>
                <input
                  id="newImage"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e)}
                  className="hidden"
                />
              </div>
              {newOccasion.imagePreview && (
                <div className="mt-2">
                  <img
                    src={newOccasion.imagePreview}
                    alt="Preview"
                    className="h-40 w-full object-cover rounded-lg"
                  />
                </div>
              )}
            </div>

            {/* Add facilities selection to the form */}
            <div className="mt-4">
              <h3 className="text-lg font-semibold mb-2">Select Facilities</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[400px] overflow-y-auto p-4 border rounded-lg">
                {facilityOptions.map((facility) => (
                  <label
                    key={facility.id}
                    className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded-md cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={newOccasion.facilities.includes(facility.id)}
                      onChange={(e) => {
                        const updated = e.target.checked
                          ? [...newOccasion.facilities, facility.id]
                          : newOccasion.facilities.filter(f => f !== facility.id)
                        setNewOccasion(prev => ({ ...prev, facilities: updated }))
                      }}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm">{facility.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <Button type="submit" disabled={uploading}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Occasion
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Existing Occasions List */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Existing Occasions</h2>
          
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {occasions.map((occasion, index) => (
                <div
                  key={occasion.id}
                  className={`border rounded-lg overflow-hidden ${
                    occasion.isVisible ? 'bg-gray-50' : 'bg-gray-100'
                  }`}
                >
                  <div className="relative">
                    <img
                      src={occasion.imagePreview || occasion.imageUrl}
                      alt={occasion.title}
                      className={`w-full h-48 object-cover ${
                        !occasion.isVisible && 'opacity-50'
                      }`}
                    />
                    <Switch
                      checked={occasion.isVisible}
                      onCheckedChange={() => handleToggleVisibility(occasion.id)}
                      className="absolute top-2 right-2"
                    />
                  </div>
                  <div className="p-4">
                    <Input
                      value={occasion.title}
                      onChange={(e) => {
                        const updatedOccasions = occasions.map(o =>
                          o.id === occasion.id ? { ...o, title: e.target.value } : o
                        )
                        setOccasions(updatedOccasions)
                      }}
                      className="mb-2"
                    />
                    <div className="flex justify-between items-center mt-2">
                      <div className="space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateOrder(occasion.id, 'up')}
                          disabled={index === 0}
                        >
                          <MoveUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateOrder(occasion.id, 'down')}
                          disabled={index === occasions.length - 1}
                        >
                          <MoveDown className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="space-x-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleUpdateOccasion(occasion.id)}
                        >
                          Update
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(occasion.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}